import React, { useEffect, useState } from 'react';
import { Container, Card, CardBody, CardHeader, Row, Col, Input } from 'reactstrap';
import SnackbarContent from '@mui/material/SnackbarContent';
import LinearProgress from '@mui/material/LinearProgress';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import Report from './OrderDetailedReport';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';

import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const ReportIndex = () => {
    const [loading, setLoading] = useState(false);
    const [isNoRecordFound, setNoRecordFound] = useState(false);
    const [isReport, setReports] = useState(null);
    const [exportData, setExportData] = useState([]);
    const [hubs, setHubs] = useState([]);
    const [riders, setRiders] = useState([]);
    const [customers, setCustomers] = useState([]);

    // Filter states
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedHub, setSelectedHub] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('');
    const [selectedRider, setSelectedRider] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [search, setSearch] = useState('');
    const [securityStatus, setSecurityStatus] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    const clearFilters = () => {
        setFromDate('');
        setToDate('');
        setSelectedHub('');
        setSelectedStatus('');
        setSelectedType('');
        setSelectedPriority('');
        setSelectedRider('');
        setSelectedCustomer('');
        setSearch('');
        setSecurityStatus('');
        setReports(null);
        setExportData([]);
        setNoRecordFound(false);
        setTotalElements(0);
        setCurrentPage(0);
    };

    useEffect(() => {
        fetchHubs();
        fetchRiders();
        fetchCustomers();
    }, []);

    const fetchHubs = async () => {
        try {
            const response = await PndRestApi.GetAllActiveHub();
            if (response.status === 200 && response.data.status === 200) {
                setHubs(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching hubs:', error);
        }
    };

    const fetchRiders = async () => {
        try {
            const response = await PndRestApi.GetAllRidersDetails();
            if (response.status === 200 && response.data.status === 200) {
                setRiders(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching riders:', error);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await PndRestApi.GetAllHubCustomers();
            if (response.status === 200 && response.data.status === 200) {
                setCustomers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        }
    };

    const generateOrderDetailsReport = async (page = 0, size = pageSize) => {
        try {
            setLoading(true);
            setNoRecordFound(false);
            if (page === 0) {
                setReports(null);
                setExportData([]);
            }

            const payload = {
                fromDate: fromDate || null,
                toDate: toDate || null,
                orderStatusId: selectedStatus || null,
                orderTypeId: selectedType || null,
                orderPriorityId: selectedPriority || null,
                selectedHubId: selectedHub || null,
                selectedRiderId: selectedRider || null,
                customerId: selectedCustomer || null,
                search: search || null,
                securityApprovalStatus: securityStatus || null,
                page: page,
                size: size,
                hubId: localStorage.getItem("hi"),
                userTypeId: localStorage.getItem("uri")
            };

            const response = await PndRestApi.GenerateOrderDetailsReport(payload);
            if (response.status === 200 && response.data.status === 200) {
                const data = response.data.data;
                if (data.content) {
                    setReports(data.content);
                    setTotalElements(data.totalElements || 0);
                    setCurrentPage(data.currentPage || page);

                    if (page === 0 && data.totalElements > 0) {
                        fetchFullDataForExport(payload);
                    }
                } else {
                    setReports(data || []);
                    setTotalElements(data?.length || 0);
                    setExportData(data || []);
                }
            } else if (response?.data?.status && response?.data?.status === 204) {
                setNoRecordFound(true);
                setReports(null);
                setTotalElements(0);
                setExportData([]);
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    `Error: ${response.data.status}`,
                    `Message: ${response.data.message}`,
                );
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification(
                "theme-light",
                "ltr",
                `Error: 500`,
                ERROR_MESSAGES.tryCatchError,
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchFullDataForExport = async (basePayload) => {
        try {
            const exportPayload = { ...basePayload, page: 0, size: 5000 };
            const response = await PndRestApi.GenerateOrderDetailsReport(exportPayload);
            if (response.status === 200 && response.data.status === 200) {
                const data = response.data.data;
                setExportData(data.content || data);
            }
        } catch (error) {
            console.error('Export fetch error:', error);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        generateOrderDetailsReport(newPage, pageSize);
    };

    const handleRowsPerPageChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(0);
        generateOrderDetailsReport(0, newSize);
    };

    return (
        <Container fluid>
            <Card className="border-0 shadow-sm mb-4">
                <CardHeader className="bg-white py-3">
                    <h5 className="mb-0 fw-bold">Order Detailed Report Filters</h5>
                </CardHeader>
                <CardBody>
                    <Row className="g-3">
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">From Date</label>
                            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">To Date</label>
                            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Store (Hub)</label>
                            <Input type="select" value={selectedHub} onChange={(e) => setSelectedHub(e.target.value)}>
                                <option value="">All Stores</option>
                                {hubs.map(hub => (
                                    <option key={hub.hubId} value={hub.hubId}>{hub.hubName}</option>
                                ))}
                            </Input>
                        </Col>
                        {/* <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Customer</label>
                            <Input type="select" value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)}>
                                <option value="">All Customers</option>
                                {customers.map(cust => (
                                    <option key={cust.hubCustomerId} value={cust.hubCustomerId}>{cust.customerName}</option>
                                ))}
                            </Input>
                        </Col> */}
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Status</label>
                            <Input type="select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                                <option value="">All Status</option>
                                {/* <option value="1">Punched</option> */}
                                {/* <option value="2">Assigned</option> */}
                                <option value="3">Full Delivered</option>
                                <option value="9">Part Delivered</option>
                                <option value="5">Undelivered</option>
                                <option value="4">Cancelled</option>
                                {/* <option value="6">Picked Up</option> */}
                            </Input>
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Order Type</label>
                            <Input type="select" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                                <option value="">All Types</option>
                                <option value="1">Drop Off</option>
                                <option value="2">Pick Up</option>
                                <option value="3">Pick & Drop</option>
                            </Input>
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Priority</label>
                            <Input type="select" value={selectedPriority} onChange={(e) => setSelectedPriority(e.target.value)}>
                                <option value="">All Priorities</option>
                                <option value="1">Express</option>
                                <option value="2">Same Day</option>
                                <option value="3">Scheduled</option>
                            </Input>
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Rider</label>
                            <Input type="select" value={selectedRider} onChange={(e) => setSelectedRider(e.target.value)}>
                                <option value="">All Riders</option>
                                {riders.map(rider => (
                                    <option key={rider.riderId} value={rider.riderId}>{rider.riderName}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Security Approval</label>
                            <Input type="select" value={securityStatus} onChange={(e) => setSecurityStatus(e.target.value)}>
                                <option value="">All Status</option>
                                <option value="1">Approved</option>
                                <option value="2">Declined</option>
                                <option value="0">Pending</option>
                            </Input>
                        </Col>
                        <Col md={3} lg={5}>
                            <label className="form-label small fw-bold text-muted">Search (Order No, Customer...)</label>
                            <Input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type to search..." />
                        </Col>
                        <Col md={3} lg={1} className="d-flex align-items-end gap-1">
                            <Tooltip title="Clear Filters" arrow>
                                <IconButton
                                    onClick={clearFilters}
                                    sx={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        '&:hover': { backgroundColor: '#e2e6ea' }
                                    }}
                                >
                                    <RestartAltIcon color="action" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Generate Report" arrow>
                                <IconButton
                                    color="primary"
                                    onClick={() => generateOrderDetailsReport(0, pageSize)}
                                    disabled={loading}
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'primary.dark' },
                                        '&.Mui-disabled': { backgroundColor: '#ccc' }
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
                                </IconButton>
                            </Tooltip>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {loading && <LinearProgress color="primary" className="mb-3" />}
            {isReport && (
                <Report
                    isReport={isReport}
                    page={currentPage}
                    rowsPerPage={pageSize}
                    totalElements={totalElements}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    exportReport={exportData}
                />
            )}
            {isNoRecordFound && <SnackbarContent message={"No Record Found"} />}
        </Container>
    );
};

export default ReportIndex;